import { useState } from "react";
import { ArrowRight, Scale, Sparkles } from "lucide-react";

import { api, apiError } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";


export default function ComparisonPanel({ clientId, lot, onApply }) {
  const { formatAmount } = useCurrency();
  const money = formatAmount;
  const [comparison, setComparison] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const compare = async () => {
    if (!clientId || !lot) {
      setMessage("Selecciona un cliente y un lote para comparar alternativas.");
      return;
    }
    setLoading(true);
    setMessage("");
    try {
      const response = await api.post("/quotes/compare", {
        client_id: clientId,
        lot_id: lot.id,
      });
      setComparison(response.data);
    } catch (error) {
      setMessage(apiError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="comparison-panel" data-testid="comparison-panel">
      <div className="panel-heading">
        <div>
          <span className="eyebrow">DECISIÓN COMERCIAL</span>
          <h2>Compara alternativas de pago</h2>
        </div>
        <button
          className="primary-button"
          data-testid="compare-options-button"
          disabled={loading}
          onClick={compare}
          type="button"
        >
          <Scale size={17} />
          {loading ? "Analizando..." : "Comparar opciones"}
        </button>
      </div>
      <p className="comparison-intro">
        Evalúa iniciales, plazos y reglas comerciales activas para elegir una propuesta equilibrada.
      </p>
      {message && <p className="form-note" data-testid="comparison-message">{message}</p>}
      {comparison && (
        <>
          <div className="recommendation-grid" data-testid="recommended-options-grid">
            {comparison.recomendadas.map(({ etiqueta, opcion }) => (
              <article className="recommendation-card" key={etiqueta}>
                <span className="recommendation-label"><Sparkles size={14} /> {etiqueta}</span>
                <strong data-testid={`recommended-option-${etiqueta.replaceAll(" ", "-").toLowerCase()}`}>
                  {opcion.meses ? `${opcion.meses} meses` : "Pago al contado"}
                </strong>
                <p>{opcion.inicial_porcentaje}% de inicial · {money(opcion.inicial)}</p>
                <dl>
                  <div><dt>Descuento</dt><dd>− {money(opcion.descuento)}</dd></div>
                  <div><dt>{opcion.meses ? "Cuota" : "Pago final"}</dt><dd>{money(opcion.meses ? opcion.valor_cuota : opcion.inicial)}</dd></div>
                </dl>
                <button
                  className="outline-button"
                  data-testid={`apply-option-${opcion.inicial_porcentaje}-${opcion.meses}`}
                  onClick={() => onApply(opcion)}
                  type="button"
                >
                  Usar esta opción <ArrowRight size={15} />
                </button>
              </article>
            ))}
          </div>
          <section className="comparison-table-panel" data-testid="comparison-table-panel">
            <div className="panel-heading">
              <h3>Todos los planes comparados</h3>
              <span>{comparison.opciones.length} alternativas</span>
            </div>
            <div className="data-table-wrap">
              <table data-testid="comparison-table">
                <thead>
                  <tr><th>Inicial</th><th>Plazo</th><th>Descuento</th><th>Cuota</th><th>Reglas</th><th /></tr>
                </thead>
                <tbody>
                  {comparison.opciones.map((option) => (
                    <tr key={`${option.inicial_porcentaje}-${option.meses}`}>
                      <td>{option.inicial_porcentaje}%</td>
                      <td>{option.meses ? `${option.meses} meses` : "Contado"}</td>
                      <td>− {money(option.descuento)}</td>
                      <td>{option.meses ? money(option.valor_cuota) : "—"}</td>
                      <td>{option.reglas_aplicadas.map((rule) => rule.nombre).join(", ") || "Plan estándar"}</td>
                      <td>
                        <button
                          aria-label="Usar plan"
                          className="icon-button comparison-apply-icon"
                          data-testid={`comparison-use-${option.inicial_porcentaje}-${option.meses}`}
                          onClick={() => onApply(option)}
                          type="button"
                        >
                          <ArrowRight size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </section>
  );
}